import { useState } from 'react';
import { trpc } from '@/lib/trpc';
import { useAuth } from '@/hooks/useAuth';
import {
  AdminTable, AdminSectionHeader, tdCls, exportCsv,
} from '@/components/admin/AdminShared';

export default function AdminUsers() {
  const { user: me } = useAuth();
  const { data: users = [], refetch, isLoading } = trpc.users.all.useQuery();
  const { data: instructors = [] }               = trpc.instructors.list.useQuery();
  const setRole = trpc.auth.setUserRole.useMutation({ onSuccess: () => refetch() });

  const [q, setQ] = useState('');
  const filtered = users.filter(u =>
    [u.name, u.phoneNumber, u.email ?? ''].join(' ')
      .toLowerCase()
      .includes(q.toLowerCase()),
  );

  const handleExport = () =>
    exportCsv(
      users.map(u => ({
        ID:        u.id,
        Name:      u.name,
        Phone:     u.phoneNumber,
        Email:     u.email ?? '',
        Age:       (u as any).age ?? '',
        Gender:    (u as any).gender ?? '',
        Role:      u.role,
        Purchases: u.purchases.length,
        Bookings:  u.bookings.length,
        Joined:    new Date(u.createdAt).toISOString(),
      })),
      'mrpanadol_users',
    );

  return (
    <div>
      <AdminSectionHeader title="Users" count={users.length} onExport={handleExport} />

      <div className="mb-4">
        <input
          value={q} onChange={e => setQ(e.target.value)}
          placeholder="Search by name, phone or email…"
          className="bg-[#18181B] border border-[#27272A] rounded-xl px-4 py-2
                     text-sm text-white placeholder-[#52525B] outline-none
                     focus:border-yellow-400/40 w-full max-w-sm"
        />
      </div>

      {isLoading ? (
        <p className="text-[#52525B] text-sm">Loading…</p>
      ) : (
        <AdminTable
          headers={['Name', 'Phone', 'Age', 'Gender', 'Role', 'Purchases', 'Bookings', 'Joined', 'Actions']}
          empty={filtered.length === 0}
        >
          {filtered.map(u => (
            <tr key={u.id} className="border-t border-white/[0.04]">
              <td className={tdCls}>
                <p className="text-white text-sm font-semibold">{u.name}</p>
                {u.email && <p className="text-[#52525B] text-xs">{u.email}</p>}
              </td>
              <td className={`${tdCls} text-[#A1A1AA] text-sm`}>{u.phoneNumber}</td>
              <td className={`${tdCls} text-[#A1A1AA] text-sm`}>{(u as any).age ?? '—'}</td>
              <td className={`${tdCls} text-[#A1A1AA] text-sm`}>
                {(u as any).gender ? (u as any).gender.replace('_', ' ') : '—'}
              </td>
              <td className={tdCls}>
                <span className={`inline-flex items-center text-[11px] font-semibold
                                 px-2 py-0.5 rounded-full border
                                 ${u.role === 'admin'
                                   ? 'bg-red-500/10 text-red-400 border-red-500/20'
                                   : u.role === 'instructor'
                                     ? 'bg-yellow-400/10 text-yellow-400 border-yellow-400/20'
                                     : 'bg-white/5 text-[#A1A1AA] border-white/10'}`}>
                  {u.role}
                </span>
              </td>
              <td className={`${tdCls} text-[#A1A1AA] text-sm`}>{u.purchases.length}</td>
              <td className={`${tdCls} text-[#A1A1AA] text-sm`}>{u.bookings.length}</td>
              <td className={`${tdCls} text-[#52525B] text-xs whitespace-nowrap`}>
                {new Date(u.createdAt).toLocaleDateString()}
              </td>
              <td className={tdCls}>
                {u.id === me?.id ? (
                  <span className="text-[#52525B] text-xs">you</span>
                ) : (
                  <div className="flex items-center gap-2 flex-wrap">
                    <select
                      value={u.role}
                      disabled={setRole.isPending}
                      onChange={e => {
                        const role = e.target.value as 'user' | 'instructor' | 'admin';
                        if (role === 'instructor') {
                          const iid = (u as any).instructorId ?? instructors[0]?.id;
                          if (!iid) {
                            alert('Create an instructor profile first (Admin → Instructors).');
                            return;
                          }
                          setRole.mutate({ userId: u.id, role, instructorId: iid });
                        } else {
                          setRole.mutate({ userId: u.id, role });
                        }
                      }}
                      className="bg-[#18181B] border border-[#27272A] rounded-lg px-2 py-1
                                 text-xs text-white outline-none focus:border-yellow-400/40"
                    >
                      <option value="user">Student</option>
                      <option value="instructor">Instructor (L2)</option>
                      <option value="admin">Admin (L1)</option>
                    </select>

                    {u.role === 'instructor' && (
                      <select
                        value={String((u as any).instructorId ?? '')}
                        disabled={setRole.isPending}
                        onChange={e =>
                          setRole.mutate({ userId: u.id, role: 'instructor', instructorId: Number(e.target.value) })
                        }
                        className="bg-[#18181B] border border-[#27272A] rounded-lg px-2 py-1
                                   text-xs text-yellow-400 outline-none focus:border-yellow-400/40 max-w-[140px]"
                        title="Which instructor profile this account manages"
                      >
                        {instructors.length === 0 && <option value="">—</option>}
                        {instructors.map(i => (
                          <option key={i.id} value={String(i.id)}>{i.name}</option>
                        ))}
                      </select>
                    )}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </AdminTable>
      )}
    </div>
  );
}
